import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useInvalidateLineRead, useLineActionCall, useLineRead, type Obj } from '@/client/api/whatsapp/line-actions';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ActionButton, ConfirmDialog, ErrorNote, Loading, selectCls, toastError } from './shared';

const ProxyTab: React.FC<{ instance: string }> = ({ instance }) => {
  const q = useLineRead<Obj>(instance, 'proxy.find');
  const call = useLineActionCall();
  const invalidate = useInvalidateLineRead();
  const [f, setF] = useState({ enabled: false, protocol: 'http', host: '', port: '', username: '', password: '' });
  const [ask, setAsk] = useState(false);
  useEffect(() => {
    if (q.data) setF((s) => ({ ...s, enabled: !!q.data!.enabled, protocol: q.data!.protocol || 'http', host: q.data!.host || '', port: String(q.data!.port ?? ''), username: q.data!.username || '', password: '' }));
  }, [q.data]);
  const portOk = /^\d{1,5}$/.test(f.port) && Number(f.port) > 0 && Number(f.port) < 65536;
  const valid = !f.enabled || (!!f.host.trim() && portOk);

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">Route this line's WhatsApp traffic through a proxy. Changing it reconnects the line.</p>
      {q.isLoading && <Loading what="the proxy settings" />}
      {q.isError && <ErrorNote error={q.error} what="the proxy settings" />}
      {q.data && (
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="h-4 w-4 accent-green-600" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} /> Use a proxy
          </label>
          <div className="grid grid-cols-[120px_1fr_100px] gap-2">
            <div><Label>Protocol</Label>
              <select className={selectCls} value={f.protocol} onChange={(e) => setF({ ...f, protocol: e.target.value })}>
                {['http', 'https', 'socks4', 'socks5'].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div><Label>Host</Label><Input value={f.host} onChange={(e) => setF({ ...f, host: e.target.value })} /></div>
            <div><Label>Port</Label><Input value={f.port} onChange={(e) => setF({ ...f, port: e.target.value })} /></div>
          </div>
          {f.port && !portOk && <p className="text-xs text-red-600">Port must be a number from 1 to 65535.</p>}
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Username (optional)</Label><Input value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} /></div>
            <div>
              <Label>Password (optional)</Label>
              <Input type="password" autoComplete="new-password" placeholder="Never shown. Type to replace." value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
            </div>
          </div>
          <ActionButton action="proxy.set" disabled={!valid} onClick={() => setAsk(true)}>Save proxy</ActionButton>
        </div>
      )}
      <ConfirmDialog
        open={ask}
        title="Change the proxy?"
        body={f.enabled ? `The line will reconnect through ${f.protocol}://${f.host}:${f.port}. It may be offline for a few seconds.` : 'The proxy is turned off and the line reconnects directly.'}
        action="Save"
        pending={call.isPending}
        onClose={() => setAsk(false)}
        onConfirm={() =>
          call.mutate(
            { instance, action: 'proxy.set', body: { enabled: f.enabled, host: f.host.trim(), port: f.port, protocol: f.protocol, username: f.username || undefined, password: f.password || undefined } },
            {
              onSuccess: () => { toast.success('Proxy saved'); setAsk(false); setF((s) => ({ ...s, password: '' })); invalidate(instance, 'proxy.find'); },
              onError: (e) => { setAsk(false); toastError(e, 'Could not save the proxy'); },
            },
          )
        }
      />
    </div>
  );
};

export default ProxyTab;
